import { FilteredItemProps } from '@delmonicos/shared-design-system-react/src/components/filtered-item/filtered-item';
import { GetTariffsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/tariffs/models/inputs/get-tariffs-paginated.model';
import { TariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/tariff/tariff.model';
import {
  ColumnDef,
  ColumnOrderState,
  VisibilityState,
  createColumnHelper,
} from '@tanstack/react-table';

import { TariffTypeFilterStateController } from '../tariff-type-filter-state/tariff-type-filter-state.controller';
import { TariffListCurrencyColumn } from './tariff-list-currency.column';
import { TariffListNameColumn } from './tariff-list-name.column';
import { TariffListOcpiTariffIdColumn } from './tariff-list-ocpi-tariff-id.column';
import { TariffListUpdatedAtColumn } from './tariff-list-updated-at.column';

// /////////////////////////////////////////////////////////////////////////////
// SORTING
// /////////////////////////////////////////////////////////////////////////////

export const TARIFF_LIST_SORT_FIELD: NonNullable<
  GetTariffsPaginatedModel['sortField']
> = 'createdAt';

export const TARIFF_LIST_SORT_ORDER: NonNullable<
  GetTariffsPaginatedModel['sortOrder']
> = 1;

// /////////////////////////////////////////////////////////////////////////////
// FILTERING
// /////////////////////////////////////////////////////////////////////////////

type ResourceListFilter = Partial<
  Record<
    keyof GetTariffsPaginatedModel,
    {
      componentType: React.ComponentType<{
        value: any;
        onRemove: FilteredItemProps['onRemove'];
      }>;
    }
  >
>;

export const TARIFF_LIST_FILTER: ResourceListFilter = {
  type: {
    componentType: TariffTypeFilterStateController,
  },
} as const;

export const TARIFF_LIST_FILTER_NAME_LIST = Object.keys(
  TARIFF_LIST_FILTER,
) as Array<keyof typeof TARIFF_LIST_FILTER>;

// /////////////////////////////////////////////////////////////////////////////
// DISPLAYING
// /////////////////////////////////////////////////////////////////////////////

export const tariffListColumnHelper = createColumnHelper<TariffModel>();

type ResourceColumnType = ColumnDef<TariffModel, any>[];

export const TARIFF_LIST_COLUMN_LIST: ResourceColumnType = [
  tariffListColumnHelper.accessor('ocpiTariffId', TariffListOcpiTariffIdColumn),
  tariffListColumnHelper.accessor('name', TariffListNameColumn),
  tariffListColumnHelper.accessor('currency', TariffListCurrencyColumn),
  tariffListColumnHelper.accessor('updatedAt', TariffListUpdatedAtColumn),
];

export const TARIFF_LIST_COLUMN_ID_LIST = TARIFF_LIST_COLUMN_LIST.flatMap(
  (columnDef) => {
    if ('accessorKey' in columnDef) {
      return [columnDef.id ?? (columnDef.accessorKey as string)];
    }

    if (columnDef.id) {
      return [columnDef.id];
    }

    return [];
  },
);

export const TARIFF_LIST_COLUMN_ORDER = [...TARIFF_LIST_COLUMN_ID_LIST];

export const TARIFF_LIST_COLUMN_VISIBILITY: VisibilityState =
  TARIFF_LIST_COLUMN_ID_LIST.reduce(
    (columnVisibility, columnId) => ({
      ...columnVisibility,
      [columnId]: true,
    }),
    {} as VisibilityState,
  );

// /////////////////////////////////////////////////////////////////////////////
// OPTIONS
// /////////////////////////////////////////////////////////////////////////////

export interface TariffListOptionsModel {
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
}

export const TARIFF_LIST_DEFAULT_OPTIONS: TariffListOptionsModel = {
  columnVisibility: TARIFF_LIST_COLUMN_VISIBILITY,
  columnOrder: TARIFF_LIST_COLUMN_ORDER,
};

export const TARIFF_LIST_COLUMN_LABEL_I18N_KEY: Record<string, string> = {
  ocpiTariffId: 'delmonicos-shared-sdk.outputs-tariff.ocpi-tariff-id',
  name: 'delmonicos-shared-sdk.outputs-tariff.name',
  currency: 'delmonicos-shared-sdk.outputs-tariff.currency',
  updatedAt: 'delmonicos-shared-sdk.outputs-base-with-timestamp.updated-at',
};
